import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Tooltip, message } from '../ui';

/**
 * Click-to-copy wrapper for an identifier.
 *
 * Codes, GSTINs, PANs, licence numbers and phone numbers exist to be pasted somewhere else — into
 * Tally, a WhatsApp message, a support ticket — and selecting a chip's text with the mouse is
 * fiddly and easy to get wrong by a character. Anything worth showing as an identifier is now worth
 * one click.
 *
 * `children` renders the value however the surrounding layout already renders it (a Tag, a Mono
 * chip, plain text); `value` is what actually reaches the clipboard, which is not always the same —
 * a mobile is displayed as "9900000054" but copied in full E.164 so it pastes usefully.
 *
 * Renders nothing but the fallback when there is no value, so callers keep their own em-dash.
 */
export function CopyValue({
  value, label, children, className, truncate,
}: {
  value?: string | null;
  /** What it is, for the tooltip: "GSTIN", "Mobile". */
  label?: string;
  children?: ReactNode;
  className?: string;
  /**
   * For a register cell whose column already truncates (`ellipsis: true`).
   *
   * The default wrapper is `inline-flex`, and a `<td class="truncate">` cannot put an ellipsis on a
   * flex child — `text-overflow` only applies to inline content — so a long device id came out hard
   * clipped mid-character with no "…". Sizing the wrapper to fit didn't fix it either: under
   * `table-layout: fixed` a `w-full` wrapper resolved wider than the cell's content box and simply
   * overflowed it.
   *
   * So in this mode the wrapper stays `display: inline` and does no truncating of its own — the
   * cell's own ellipsis, which already worked for plain text, is left to do the job.
   */
  truncate?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  if (!value) return <>{children ?? <span className="font-normal text-subtle">—</span>}</>;

  const copy = async (e: { stopPropagation: () => void }) => {
    e.stopPropagation(); // never let a copy also trigger the row/card underneath
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard access can be refused (insecure origin, permissions) — say so rather than
      // leaving a click that silently did nothing.
      message.error('Could not copy — select the text manually');
    }
  };

  return (
    <Tooltip label={copied ? 'Copied' : `${label ?? 'Value'} — click to copy`} inline={truncate}>
      <span
        role="button"
        tabIndex={0}
        aria-label={`Copy ${label ?? 'value'}`}
        onClick={copy}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copy(e); } }}
        className={`cursor-pointer rounded outline-offset-2 ${truncate ? 'inline' : 'inline-flex items-center gap-1.5'} ${className ?? ''}`}
      >
        {children ?? value}
        {/*
          No tick in truncate mode. The tick is an atomic inline box, so adding it to an
          already-overflowing cell re-flows the line and costs it its "…" — the value looked
          MORE clipped for having been copied. There the tooltip flipping to "Copied", right
          under the cursor, is the confirmation.
        */}
        {copied && !truncate && <Check className="size-3.5 shrink-0 text-success-600 dark:text-success-500" aria-hidden />}
      </span>
    </Tooltip>
  );
}
